<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import {
  useOrgWorkflows,
  useSavedCallRuns,
  useSavedCallBaseline,
  useSetSavedCallBaseline,
  useClearSavedCallBaseline,
  type CountMatch,
  type SavedCall,
} from "@/queries/saved-calls";
import { useProjectWorkflows } from "@/queries/projects";
import { useWorkflowInputKeys } from "@/queries/workflows";
import { useAlertStore } from "@/stores/alerts-store";
import ValidationDiffTable from "./ValidationDiffTable.vue";
import { getRunStatusBadgeClass } from "@/views/builder/utils/runFormatters";

interface Props {
  show: boolean;
  // When set, the modal is in edit mode for this call; otherwise it's a create form.
  editing?: SavedCall | null;
  pending?: boolean;
  errorMessage?: string | null;
  // When set, the create form is scoped to this project: the workflow picker lists only this project's
  // workflows and the created call is stamped to it. Unset → the org-wide picker (top-level Testing page).
  projectId?: string;
  // All existing calls, so the auto-suggested name can avoid colliding with another call on the same
  // workflow (bumps to "… test (2)", "(3)", …). Suggestion-only — duplicates aren't blocked.
  existingCalls?: SavedCall[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "save", payload: SaveSavedCallPayload): void;
}>();

export interface SaveSavedCallPayload {
  projectId: string;
  workflowId: string;
  name: string;
  description: string;
  inputs: Record<string, unknown>;
}

const alertStore = useAlertStore();

const isEdit = computed(() => !!props.editing);
// Project-scoped when embedded in a project's Testing tab; org-wide on the top-level Testing page.
const isProjectScoped = computed(() => !!props.projectId);

// Workflow picker source. Two shapes normalised to one:
//  • project-scoped → this project's own workflows (useProjectWorkflows), only while the modal is open;
//  • hub → the org's workflows across every project (useOrgWorkflows).
const scopedProjectIdRef = computed(() => props.projectId ?? "");
const projectWorkflowsEnabled = computed(() => !!props.projectId && props.show);
const { projectWorkflows, projectWorkflowsLoading } = useProjectWorkflows(scopedProjectIdRef, projectWorkflowsEnabled);
const { orgWorkflows, orgWorkflowsLoading } = useOrgWorkflows();

interface PickerWorkflow { workflowId: string; workflowName: string; projectId: string; projectName: string; }
const pickerWorkflows = computed<PickerWorkflow[]>(() =>
  isProjectScoped.value
    ? projectWorkflows.value.map((w) => ({
        workflowId: w.id,
        workflowName: w.name,
        projectId: w.projectId,
        projectName: "", // same project for all — the project name is redundant when scoped
      }))
    : orgWorkflows.value
);
const pickerLoading = computed(() =>
  isProjectScoped.value ? projectWorkflowsLoading.value : orgWorkflowsLoading.value
);

// Form state.
const selectedWorkflowId = ref<string>("");
const name = ref("");
const description = ref("");

// The last name we auto-suggested from the selected workflow. Lets the suggestion watcher overwrite
// its own prior suggestion when the workflow changes, while never clobbering a name the user typed.
const suggestedName = ref("");
// Per-key JSON draft + parse errors (mirrors RunWorkflowModal / DataStoresTab).
const drafts = ref<Record<string, string>>({});
const parseErrors = ref<Record<string, string | null>>({});

// The owning project: the scoped project when embedded, else derived from the selected workflow.
const selectedWorkflow = computed(() =>
  pickerWorkflows.value.find((w) => w.workflowId === selectedWorkflowId.value) ?? null
);
const derivedProjectId = computed(
  () => props.editing?.projectId ?? props.projectId ?? selectedWorkflow.value?.projectId ?? ""
);

// The first "{workflow} test" / "{workflow} test (n)" not already taken by another call on the SAME
// workflow. Case-insensitive; scoped per-workflow because that's how a saved call is scoped.
function suggestNameFor(workflowId: string, workflowName: string): string {
  const taken = new Set(
    (props.existingCalls ?? [])
      .filter((c) => c.workflowId === workflowId)
      .map((c) => c.name.trim().toLowerCase())
  );
  const base = `${workflowName} test`;
  if (!taken.has(base.toLowerCase())) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base} (${n})`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

// Auto-suggest a name from the chosen workflow (create mode only). Fills an empty field, or replaces
// a name we ourselves suggested — but leaves a name the user has typed untouched.
watch(selectedWorkflow, (workflow) => {
  if (isEdit.value || !workflow) return;
  const untouched = name.value.trim() === "" || name.value === suggestedName.value;
  if (!untouched) return;
  const suggestion = suggestNameFor(workflow.workflowId, workflow.workflowName);
  name.value = suggestion;
  suggestedName.value = suggestion;
});

const projectIdRef = computed(() => derivedProjectId.value);
const workflowIdRef = computed(() => selectedWorkflowId.value || null);
const { inputKeys, inputKeysLoading } = useWorkflowInputKeys(projectIdRef, workflowIdRef);

const keys = computed(() => inputKeys.value?.keys ?? []);

function serialize(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return JSON.stringify(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// Reset the form each time the modal opens.
watch(
  () => props.show,
  (show) => {
    if (!show) return;
    if (props.editing) {
      selectedWorkflowId.value = props.editing.workflowId;
      name.value = props.editing.name;
      description.value = props.editing.description ?? "";
    } else {
      selectedWorkflowId.value = "";
      name.value = "";
      description.value = "";
    }
    suggestedName.value = "";
    // Reset the baseline sub-form each open.
    selectedRunId.value = "";
    selectedCountPolicy.value = "NoErrors";
    // Clear drafts on every open so the hydration watch reseeds cleanly from props.editing.inputs.
    // Without this, drafts from a previous open leak into the next (shared key names across calls).
    drafts.value = {};
    parseErrors.value = {};
  },
  { immediate: true }
);

// Hydrate the input drafts whenever the declared keys resolve (seeding from the call when editing).
// drafts is cleared on open (above), so the `drafts.value[key]` fallback only preserves values the
// user typed within the CURRENT open session (e.g. while keys resolve), never across opens.
watch(
  [keys, () => props.show],
  ([currentKeys, show]) => {
    if (!show) return;
    const fresh: Record<string, string> = {};
    const existing = props.editing?.inputs ?? {};
    for (const key of currentKeys) {
      fresh[key] = drafts.value[key] ?? (key in existing ? serialize(existing[key]) : "");
    }
    drafts.value = fresh;
  },
  { immediate: true }
);

// ── Baseline sub-form (edit mode only — a baseline needs an existing call with runs) ─────────────
const editingId = computed(() => props.editing?.id ?? null);
const { runs, runsLoading } = useSavedCallRuns(editingId);
const { baseline } = useSavedCallBaseline(editingId);
const { setBaselineAsync, setBaselinePending } = useSetSavedCallBaseline();
const { clearBaselineAsync, clearBaselinePending } = useClearSavedCallBaseline();

const selectedRunId = ref<string>("");
const selectedCountPolicy = ref<CountMatch>("NoErrors");

// The per-service "what this baseline expects" table is collapsed by default to keep the modal short.
const showExpectations = ref(false);

// Human-readable label for the baseline's source run — resolve its id against the loaded runs so we
// show "{status} on {date}" instead of a raw GUID. Falls back to the short id if the run has aged out
// of the recent-runs window.
const baselineSourceLabel = computed(() => {
  const srcId = baseline.value?.sourceRunId;
  if (!srcId) return "";
  const run = runs.value.find((r) => r.runId === srcId);
  return run
    ? `${run.status} on ${new Date(run.startedAt).toLocaleString()}`
    : `run ${srcId.slice(0, 8)}…`;
});

// Deep-link into the run this baseline was captured from — the project's Run History tab.
const baselineRunLink = computed(() => {
  const srcId = baseline.value?.sourceRunId;
  if (!srcId || !derivedProjectId.value || !selectedWorkflowId.value) return null;
  return `/projects/${derivedProjectId.value}/history/${selectedWorkflowId.value}/${srcId}`;
});

// One-line explanation of each count policy so the dropdown isn't inscrutable.
const countPolicyHelp: Record<CountMatch, string> = {
  NoErrors: "Pass as long as no service has new errors or warnings beyond the baseline (recommended).",
  Exact: "Every service's success/warning/error counts must match the baseline exactly.",
  AtLeast: "Each service must have at least the baseline's successes and no more errors/warnings.",
  Ignore: "Only the pass/fail status is compared; counts are ignored.",
};

async function setBaseline() {
  const callId = props.editing?.id;
  if (!callId) return;
  try {
    await setBaselineAsync({
      callId,
      // Empty → the backend promotes the call's last run.
      runId: selectedRunId.value || undefined,
      countPolicy: selectedCountPolicy.value,
    });
    alertStore.addSuccess("Baseline", "Baseline set for this call.");
  } catch {
    alertStore.addError("Baseline", "Couldn't set the baseline. Please try again.");
  }
}

async function clearBaseline() {
  const callId = props.editing?.id;
  if (!callId) return;
  try {
    await clearBaselineAsync(callId);
    alertStore.addSuccess("Baseline", "Baseline cleared. This call's runs won't be validated until you set a new one.");
  } catch {
    alertStore.addError("Baseline", "Couldn't clear the baseline. Please try again.");
  }
}

function close() {
  if (props.pending) return;
  emit("update:show", false);
}

const canSave = computed(
  () => name.value.trim() !== "" && selectedWorkflowId.value !== "" && derivedProjectId.value !== ""
);

function save() {
  if (!canSave.value) return;

  const inputs: Record<string, unknown> = {};
  const errors: Record<string, string | null> = {};
  let hasError = false;
  for (const key of keys.value) {
    const raw = (drafts.value[key] ?? "").trim();
    if (raw === "") continue;
    try {
      inputs[key] = JSON.parse(raw);
      errors[key] = null;
    } catch (e) {
      errors[key] = (e as Error).message || "Invalid JSON";
      hasError = true;
    }
  }
  parseErrors.value = errors;
  if (hasError) return;

  emit("save", {
    projectId: derivedProjectId.value,
    workflowId: selectedWorkflowId.value,
    name: name.value.trim(),
    description: description.value.trim(),
    inputs,
  });
}
</script>

<template>
  <div v-if="show" class="modal fade show d-block" tabindex="-1" @click="close">
    <div class="modal-dialog modal-lg" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title mb-0">{{ isEdit ? "Edit saved call" : "New saved call" }}</h2>
          <button type="button" class="btn-close" :disabled="pending" @click="close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label small fw-semibold">Workflow</label>
            <!-- Edit mode: the workflow is fixed, so just show it. Reading it from the call itself
                 (not the picker list) means it renders even if the workflow is no longer in that
                 list — the disabled <select> used to show blank in that case. -->
            <template v-if="isEdit">
              <input
                type="text"
                class="form-control"
                :value="editing
                  ? `${editing.workflowName ?? 'Unknown workflow'}${editing.projectName ? ' — ' + editing.projectName : ''}`
                  : ''"
                disabled
              />
              <div class="form-text">The workflow can't be changed after creation.</div>
            </template>
            <!-- Create mode: pick from the project's workflows (scoped) or the org's (hub). -->
            <select v-else v-model="selectedWorkflowId" class="form-select" :disabled="pickerLoading">
              <option value="" disabled>
                {{ pickerLoading ? "Loading workflows…" : "Select a workflow…" }}
              </option>
              <option v-for="w in pickerWorkflows" :key="w.workflowId" :value="w.workflowId">
                {{ w.workflowName }}<template v-if="w.projectName"> — {{ w.projectName }}</template>
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label small fw-semibold">Name</label>
            <input v-model="name" type="text" class="form-control" placeholder="e.g. Nightly smoke test" />
          </div>

          <div class="mb-3">
            <label class="form-label small fw-semibold">Description <span class="text-muted">(optional)</span></label>
            <textarea v-model="description" class="form-control" rows="2"></textarea>
          </div>

          <div v-if="selectedWorkflowId">
            <label class="form-label small fw-semibold mb-2">Inputs</label>
            <div v-if="inputKeysLoading" class="text-muted small">
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading inputs…
            </div>
            <p v-else-if="keys.length === 0" class="text-muted small mb-0">
              This workflow declares no inputs.
            </p>
            <div v-else class="d-flex flex-column gap-3">
              <div v-for="key in keys" :key="key">
                <label class="form-label small mb-1">{{ key }}</label>
                <textarea
                  v-model="drafts[key]"
                  class="form-control font-monospace small"
                  rows="2"
                  spellcheck="false"
                  placeholder='e.g. "value" or { "id": 1 }'
                ></textarea>
                <div v-if="parseErrors[key]" class="text-danger small mt-1">
                  <i class="bi bi-exclamation-triangle me-1"></i>{{ parseErrors[key] }}
                </div>
              </div>
              <p class="text-muted small mb-0">Each value is parsed as JSON. Leave blank to omit.</p>
            </div>
          </div>

          <!-- Baseline -->
          <div class="mt-4 pt-3 border-top">
            <div v-if="isEdit">
              <label class="form-label small fw-semibold mb-2">Baseline</label>
              <div class="d-flex align-items-center gap-2 mb-2">
                <p class="small mb-0 flex-grow-1">
                  <template v-if="baseline">
                    Current baseline: <span class="fw-semibold">{{ baseline.countPolicy }}</span>
                    <span v-if="baselineSourceLabel" class="text-muted">
                      (from
                      <router-link
                        v-if="baselineRunLink"
                        :to="baselineRunLink"
                        title="View the run this baseline was captured from"
                      >{{ baselineSourceLabel }}</router-link>
                      <template v-else>{{ baselineSourceLabel }}</template>)
                    </span>
                  </template>
                  <span v-else class="text-muted">No baseline set.</span>
                </p>
                <button
                  v-if="baseline"
                  type="button"
                  class="bb-btn-danger btn-sm"
                  :disabled="clearBaselinePending"
                  title="Remove this baseline — the call's runs won't be validated until a new one is set"
                  @click="clearBaseline"
                >
                  <span v-if="clearBaselinePending" class="spinner-border spinner-border-sm" role="status"></span>
                  <template v-else><i class="bi bi-x-lg me-1"></i>Clear</template>
                </button>
              </div>

              <div v-if="baseline && baseline.services.length" class="mb-3">
                <button
                  type="button"
                  class="btn btn-sm btn-link p-0 text-muted"
                  :aria-expanded="showExpectations"
                  @click="showExpectations = !showExpectations"
                >
                  <small>
                    <i class="bi me-1" :class="showExpectations ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                    Expected values ({{ baseline.services.length }} service{{ baseline.services.length === 1 ? "" : "s" }})
                  </small>
                </button>
                <div v-if="showExpectations" class="mt-2">
                  <p class="small mb-2">
                    Overall workflow status:
                    <span class="badge" :class="getRunStatusBadgeClass(baseline.expectedWorkflowStatus)">
                      {{ baseline.expectedWorkflowStatus }}
                    </span>
                  </p>
                  <ValidationDiffTable mode="expected" :expectations="baseline.services" />
                </div>
              </div>

              <template v-if="runsLoading">
                <div class="text-muted small">
                  <span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading runs…
                </div>
              </template>
              <p v-else-if="runs.length === 0" class="text-muted small mb-0">
                Run this call once before setting a baseline.
              </p>
              <div v-else class="row g-2 align-items-end">
                <div class="col-12 col-md-6">
                  <label class="form-label small mb-1">Run</label>
                  <select v-model="selectedRunId" class="form-select form-select-sm">
                    <option value="">Last run</option>
                    <option v-for="r in runs" :key="r.runId" :value="r.runId">
                      {{ r.status }} — {{ new Date(r.startedAt).toLocaleString() }}
                    </option>
                  </select>
                </div>
                <div class="col-8 col-md-4">
                  <label class="form-label small mb-1">Count policy</label>
                  <select v-model="selectedCountPolicy" class="form-select form-select-sm">
                    <option value="NoErrors">NoErrors</option>
                    <option value="Exact">Exact</option>
                    <option value="AtLeast">AtLeast</option>
                    <option value="Ignore">Ignore</option>
                  </select>
                </div>
                <div class="col-4 col-md-2">
                  <button
                    type="button"
                    class="bb-btn-secondary-cta btn-sm w-100"
                    :disabled="setBaselinePending"
                    @click="setBaseline"
                  >
                    <span v-if="setBaselinePending" class="spinner-border spinner-border-sm" role="status"></span>
                    <template v-else>{{ baseline ? "Replace" : "Set baseline" }}</template>
                  </button>
                </div>
                <div class="col-12">
                  <div class="form-text mt-0">{{ countPolicyHelp[selectedCountPolicy] }}</div>
                </div>
              </div>
            </div>

            <p v-else class="text-muted small mb-0">
              <label class="form-label small fw-semibold mb-1">Baseline</label><br />
              Save the call and run it once, then edit it to set a validation baseline.
            </p>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-3 mb-0">
            <i class="bi bi-exclamation-circle me-2"></i>{{ errorMessage }}
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="bb-btn-secondary-cta" :disabled="pending" @click="close">
            Cancel
          </button>
          <button type="button" class="bb-btn-primary-cta" :disabled="pending || !canSave" @click="save">
            <span v-if="pending" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <i v-else class="bi bi-save me-1"></i>
            {{ isEdit ? "Save changes" : "Create" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
